import { Component, ElementRef, HostListener, Input, OnChanges, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import { ThemeService } from '@wind-log_library/fe-component-library';
import { optimizeChartResolution } from 'app/shared/helper/canvas-chart-resolution-optimize';
import { DateTimeFormatPreferencePipe } from 'app/shared/pipes/date-time-format-preference/date-time-format-preference.pipe';
import { NoUnderscorePipe } from 'app/shared/pipes/no-underscore/no-underscore.pipe';
import { HelperService } from 'app/shared/services/helper-service/helper.service';
import moment from 'moment';
import { ReplaySubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-availability-heatmap-canvas',
  templateUrl: './availability-heatmap-canvas.component.html',
  styleUrls: ['./availability-heatmap-canvas.component.scss'],
  standalone: false,
})
export class AvailabilityHeatmapCanvasComponent implements OnInit, OnChanges, OnDestroy {
  @Input() level;
  @Input() daysCount: number;
  @Input() from;
  @Input() availabilities: any[] = [];
  @Input() chartType: ChartType = ChartType.Percentage;
  @Input() timeStamps: number[];
  @Input() timezone: string;

  @ViewChild('canvas', { static: true }) canvas: ElementRef;
  @ViewChild('container', { static: true }) container: ElementRef;
  @ViewChild('tooltip', { static: true }) tooltipElement: ElementRef;

  public toolTipShow = false;
  public popupLeft = 0;
  public popupTop = 0;
  public clickable = false;
  public tooltipAssetName = '';
  public tooltipDate = '';
  public tooltipValue = '';

  public loading = false;
  public chartTypeString: string = 'Rated power';

  private rowHeight = 20;
  private marginRight = 1;
  private labelWidth = 56;
  private subSize;
  private size = new ReplaySubject();
  private canvasWidth;
  private canvasHeight;

  private unsubscriber = new ReplaySubject();
  tooltipLabel: string;

  constructor(
    private router: Router,
    private helper: HelperService,
    private themeService: ThemeService,
    private translateService: TranslateService,
    private dateTimeFormatPreferencePipe: DateTimeFormatPreferencePipe,
  ) {}

  ngOnInit() {
    moment.locale(this.translateService.currentLang);
    this.subscribeOnThemeChanges();
    this.translateService.onLangChange.pipe(takeUntil(this.unsubscriber)).subscribe((event: LangChangeEvent) => {
      this.loading = true;
      if (event) {
        moment.locale(this.translateService.currentLang);
        this.reset();
        this.loading = false;
        this.getData();
      }
    });
  }

  ngOnChanges() {
    this.loading = true;
    const labelRatedPower = this.translateService.instant('FORECAST.OVERVIEW.CHARTS.RATED_POWER');
    const labelStatus = this.translateService.instant('FORECAST.OVERVIEW.CHARTS.STATUS');
    this.tooltipLabel = this.chartType === ChartType.Percentage ? labelRatedPower : labelStatus;
    if (this.availabilities) {
      this.reset();
      this.loading = false;
      this.getData();
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.size.next({
      width: this.container.nativeElement.offsetWidth,
      height: this.rowHeight * (this.availabilities ? this.availabilities.length : 2) + this.rowHeight + 1,
    });
  }

  getData() {
    this.sort(this.availabilities);
    this.size.subscribe((size: any) => {
      this.canvasWidth = this.canvas.nativeElement.width = size.width;
      this.canvasHeight = this.canvas.nativeElement.height = size.height;
      optimizeChartResolution(this.canvas.nativeElement.getContext('2d'), size.width, size.height);
      this.drawChart();
    });

    this.onResize();
  }

  private getStringForItem(item: number): string {
    if (this.chartType === ChartType.Percentage) {
      if (item < 0.2) {
        return '0%';
      } else if (item < 0.4) {
        return '20%';
      } else if (item < 0.6) {
        return '40%';
      } else if (item < 0.8) {
        return '60%';
      } else if (item < 1) {
        return '80%';
      } else {
        return '100%';
      }
    } else if (this.chartType === ChartType.Status) {
      if (item === 0) {
        return 'Available';
      } else if (item === 1) {
        return 'Power Limitation';
      } else if (item === 2) {
        return 'In service';
      }
    }
  }
  drawChart() {
    if (!this.availabilities?.length) {
      return;
    }
    const step = this.daysCount > 1 ? 3600000 : 600000;
    const ctx = this.canvas.nativeElement.getContext('2d');

    const labels = this.helper.generateChartLabels(
      this.timeStamps,
      this.daysCount * 24,
      false,
      true,
      this.daysCount === 7 ? 'DD MMM' : 'HH:mm',
    );

    const itemWidth =
      (this.canvasWidth - this.labelWidth - this.marginRight) / this.availabilities[0].availability.length;
    ctx.textAlign = 'center';
    this.availabilities.forEach((e, j) => {
      let font = 12 + 'px SF Display';
      ctx.beginPath();
      ctx.lineWidth = '0.75';
      ctx.fillStyle = this.themeService.getColor('--primary-background');
      ctx.fillRect(0, this.rowHeight * j + 1.5, this.labelWidth, this.rowHeight - 1.5);
      ctx.stroke();
      ctx.font = font;
      ctx.fillStyle = this.themeService.getColor('--white');
      ctx.fillText((e.row.displayCode || e.row.code).replace(/_/g, ' '),this.labelWidth / 2, this.rowHeight * (j + 1) - this.rowHeight / 4);
      ctx.stroke();
      ctx.closePath();

      e.availability.forEach((item, i) => {
        ctx.beginPath();
        ctx.strokeStyle = this.themeService.getColor('--card');
        ctx.rect(
          this.labelWidth + this.marginRight + itemWidth * i,
          this.rowHeight * j + 1.5,
          itemWidth,
          this.rowHeight - 1.5,
        );

        ctx.fillStyle = this.getColorForItem(item);

        ctx.fill();
        ctx.stroke();
        ctx.closePath();

        if (j === this.availabilities.length - 1 && labels[i]) {
          ctx.beginPath();
          ctx.fillStyle = '#999999';
          ctx.fillText(
            labels[i],
            this.labelWidth + itemWidth * i + this.marginRight,
            this.rowHeight * (j + 1) + this.rowHeight,
          );
          ctx.closePath();
        }
      });
    });
  }

  chartHovered(e) {
    const cont = this.canvas.nativeElement;
    const [x, y] = [e.offsetX, e.offsetY];
    const tooltipWidth = this.toolTipShow ? this.tooltipElement.nativeElement.offsetWidth : 170;
    const tooltipHeight = this.toolTipShow ? this.tooltipElement.nativeElement.offsetHeight : 90;
    this.toolTipShow = y < cont.offsetHeight - 20 && x < cont.offsetWidth - this.marginRight;
    if (!this.toolTipShow) return this.chartLeave(e);
    this.popupLeft = e.clientX - tooltipWidth / 2;
    this.popupTop = e.clientY - tooltipHeight - 10;
    this.clickable =
      e.offsetX < this.labelWidth + this.marginRight &&
      e.offsetY <
        e.srcElement.clientHeight - (2 * e.srcElement.clientHeight) / this.availabilities.length + this.rowHeight;
    this.computeTooltipText(e);
  }

  computeTooltipText(e) {
    if (!this.availabilities?.length && !this.availabilities?.[0]?.availability.length) return;
    const chartWidth = e.srcElement.clientWidth;
    const itemWidth = (chartWidth - this.labelWidth - this.marginRight) / this.availabilities[0].availability.length;
    const row = Math.abs(Math.min(Math.floor(e.offsetY / this.rowHeight), this.availabilities.length - 1));
    const col = Math.abs(
      Math.min(
        Math.floor((e.offsetX - this.labelWidth - this.marginRight) / itemWidth),
        this.availabilities[row].availability.length - 1,
      ),
    );
    const item = this.availabilities[row].availability[col];
    const step = this.daysCount > 1 ? 3600000 : 600000;
    this.tooltipDate = this.dateTimeFormatPreferencePipe.transform(this.from + col * step, 'dmt', this.timezone);
    this.tooltipAssetName =
      this.availabilities[row].row.displayName ||
      (this.availabilities[row].row.displayCode || this.availabilities[row].row.code)?.replace(/_/g, ' ');
    this.tooltipValue = this.getStringForItem(item);
  }

  chartClicked(e) {
    if (!this.clickable || this.level !== 'portfolio') return;
    const chartHeight = e.srcElement.clientHeight;
    if (
      e.offsetX < this.labelWidth &&
      e.offsetY < chartHeight - (2 * chartHeight) / (this.availabilities.length + this.rowHeight)
    ) {
      const park =
        this.availabilities[Math.floor(e.offsetY / (chartHeight / (this.availabilities.length + 1)))].row.name;
      this.router.navigate(['/forecast/park/' + park + '/overview']);
    }
  }

  sort(arr) {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
    arr.sort((a, b) => {
      if (collator.compare(a.row.code, b.row.code) > 0) {
        return 1;
      }
      if (collator.compare(a.row.code, b.row.code) < 0) {
        return -1;
      }
      return 0;
    });
  }

  reset() {
    this.canvas.nativeElement
      .getContext('2d')
      .clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
  }

  private getColorForItem(item: number): string {
    if (this.chartType === ChartType.Percentage) {
      if (item < 0.2) {
        return this.themeService.getColor('--chart-availability-percentage-1');
      } else if (item < 0.4) {
        return this.themeService.getColor('--chart-availability-percentage-2');
      } else if (item < 0.6) {
        return this.themeService.getColor('--chart-availability-percentage-3');
      } else if (item < 0.8) {
        return this.themeService.getColor('--chart-availability-percentage-4');
      } else if (item < 1) {
        return this.themeService.getColor('--chart-availability-percentage-5');
      } else {
        return this.themeService.getColor('--chart-availability-percentage-6');
      }
    } else if (this.chartType === ChartType.Status) {
      if (item === 0) {
        return this.themeService.getColor('--chart-availability-status-1');
      } else if (item === 1) {
        return this.themeService.getColor('--chart-availability-status-2');
      } else if (item === 2) {
        return this.themeService.getColor('--chart-availability-status-3');
      }
    }
  }

  private subscribeOnThemeChanges(): void {
    this.themeService
      .getTheme()
      .pipe(takeUntil(this.unsubscriber))
      .subscribe(() => {
        this.drawChart();
      });
  }

  ngOnDestroy() {
    if (this.subSize) {
      this.subSize.unsubscribe();
    }
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
  }

  public chartLeave(event: MouseEvent) {
    this.toolTipShow = false;
  }
}

export enum ChartType {
  Percentage = 1,
  Status = 2,
}
